import { createStore } from '@bigo/reactive-store';
import * as Sentry from '@sentry/browser';
import { pick } from 'lodash-es';

import businessPermissionApi from '@/apis/business-permission';
import userApi from '@/apis/user';
import { AuthType } from '@/constants/auth-type';
import { BusinessPermission } from '@/models/business-permission';

export type BusinessPermissionDetail = {
  /** 组别 */
  departments: Set<string> | 'ALL';
  /** 地区 */
  areas: Set<string> | 'ALL';
  /** 权限类型 */
  authType: AuthType;
};

export type LoginInfoState = {
  /** CAS账号 */
  casId: string;
  /** 用户名 */
  casName: string;
  /** 菜单权限 */
  permission: string[];
  /** 业务权限 */
  businessPermission: Map<string, BusinessPermissionDetail>;
};

const loginInfoStore = createStore({
  state: {
    casId: '',
    casName: '',
    permission: [],
    businessPermission: new Map(),
  } as LoginInfoState,
  actions: {
    /** 获取登录用户信息 */
    async loadLoginInfo() {
      const result = await userApi.getLoginInfo();

      this.casId = result.casId;
      this.casName = result.casName;
      this.permission = result.permission;

      Sentry.configureScope((scope) => {
        scope.setUser(pick(this, ['casId', 'casName', 'permission']));
      });
    },
    /** 获取登录用户的业务权限 */
    async loadBusinessPermission() {
      const data = { cas: this.casId, pageSize: 1000 };
      const result = await businessPermissionApi.list({ ...data, pageIndex: 1 });
      const totalPage = Math.ceil(result.page.totalRecord / data.pageSize);
      const list = result.list;

      await Promise.all(
        Array.from({ length: totalPage - 1 }).map(async (_, i) => {
          const pageIndex = i + 2;
          const r = await businessPermissionApi.list({ ...data, pageIndex });
          list.push(...r.list);
        }),
      );

      result.list.forEach((item: BusinessPermission) => {
        const { productCode, departmentCode, areaCode } = item;
        const record = this.businessPermission.get(productCode) || {
          departments: 'ALL',
          areas: 'ALL',
          authType: item.authType,
        };

        record.departments = departmentCode === 'ALL' ? 'ALL' : new Set(departmentCode.split(','));
        record.areas = areaCode === 'ALL' ? 'ALL' : new Set(areaCode.split(','));

        this.businessPermission.set(productCode, record);
      });
    },
    async load() {
      await this.loadLoginInfo();
      await this.loadBusinessPermission();
    },
    /** 判断是否拥有对应业务权限 */
    hasAuthType(productCode: string | undefined, authType: AuthType) {
      const products =
        !productCode || productCode === 'ALL' ? [...this.businessPermission.keys()] : [productCode];

      return products.every((code) => {
        const ownAuthType = this.businessPermission.get(code)?.authType;
        return ownAuthType && ownAuthType >= authType;
      });
    },
    /** 判断是否拥有导出权限（中级权限） */
    canExport(productCode?: string) {
      return this.hasAuthType(productCode, AuthType.Middle);
    },
    /** 判断是否拥有导出权限（高级权限） */
    canEdit(productCode?: string) {
      return this.hasAuthType(productCode, AuthType.High);
    },
  },
});

export default loginInfoStore;
